import React, { useState, useEffect } from "react";
import {
  Users, DollarSign, CheckCircle2, Calendar,
  RefreshCw, Loader2, ChevronDown, ChevronUp, Download,
  FileSpreadsheet, TrendingUp, TrendingDown, FileText, AlertTriangle, Wallet
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Button } from "../../components/ui/button";

const MONTH_NAMES = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const formatK = (v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(Math.round(v));
const formatFull = (v) => new Intl.NumberFormat("fr-FR").format(Math.round(v || 0)) + " FCFA";

export default function MonthlyPaymentsTab({ data, loading, year, month, onYearChange, onMonthChange, onLoad, onDownload, downloading, expandedClient, onToggleClient }) {
  useEffect(() => { onLoad(year, month); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const displayMonths = data?.months?.slice(-6) || [];

  return (
    <div className="space-y-6" data-testid="payments-tab">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-slate-900 border border-white/10 rounded-xl px-3 py-2">
            <Calendar className="w-4 h-4 text-cyan-400" />
            <select value={month} onChange={e => onMonthChange(+e.target.value)}
              className="bg-transparent text-white text-sm focus:outline-none" data-testid="mp-month-select">
              {MONTH_NAMES.slice(1).map((n, i) => <option key={i+1} value={i+1} className="bg-slate-900">{n}</option>)}
            </select>
            <select value={year} onChange={e => onYearChange(+e.target.value)}
              className="bg-transparent text-white text-sm focus:outline-none" data-testid="mp-year-select">
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y} className="bg-slate-900">{y}</option>)}
            </select>
          </div>
          <Button onClick={() => onLoad(year, month)} disabled={loading}
            className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="mp-load-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-2">Charger</span>
          </Button>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => onDownload("pdf")} disabled={downloading || !data}
            className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="mp-download-pdf">
            <Download className="w-4 h-4" /> PDF
          </Button>
          <Button onClick={() => onDownload("csv")} disabled={downloading || !data}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="mp-download-csv">
            <FileSpreadsheet className="w-4 h-4" /> CSV / Excel
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { label: "Clients", value: data.summary.total_clients, icon: Users, color: "cyan" },
              { label: "Total Facturé", value: formatFull(data.summary.total_invoiced), icon: FileText, color: "white", small: true },
              { label: "Total Payé", value: formatFull(data.summary.total_paid), icon: CheckCircle2, color: "emerald", small: true },
              { label: "Reste à Payer", value: formatFull(data.summary.total_remaining), icon: AlertTriangle, color: "amber", small: true },
              { label: "Taux Moyen", value: `${data.summary.avg_recovery_rate}%`, icon: TrendingUp, color: data.summary.avg_recovery_rate >= 80 ? "emerald" : data.summary.avg_recovery_rate >= 50 ? "amber" : "red" },
              { label: "> 80%", value: data.summary.clients_above_80, icon: TrendingUp, color: "emerald" },
              { label: "< 50%", value: data.summary.clients_below_50, icon: TrendingDown, color: "red" },
            ].map((kpi) => (
              <div key={kpi.label} className="bg-slate-900 border border-white/10 rounded-xl p-3 text-center">
                <kpi.icon className={`w-4 h-4 mx-auto mb-1 text-${kpi.color}-400`} />
                <p className="text-[10px] text-slate-500 uppercase">{kpi.label}</p>
                <p className={`${kpi.small ? 'text-sm' : 'text-xl'} font-bold text-${kpi.color}-400`}>{kpi.value}</p>
              </div>
            ))}
          </div>

          {/* Monthly trend mini chart */}
          <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-slate-400 uppercase mb-3">Évolution mensuelle (12 derniers mois)</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={(data.months || []).map(m => {
                const gt = data.grand_totals[m] || {};
                const parts = m.split("-");
                return {
                  name: MONTH_NAMES[parseInt(parts[1])]?.slice(0, 3) + " " + parts[0].slice(2),
                  facture: gt.invoiced || 0,
                  paye: gt.paid || 0,
                };
              })}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="name" stroke="#94a3b8" fontSize={10} />
                <YAxis stroke="#94a3b8" fontSize={10} tickFormatter={v => formatK(v)} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#1e293b" }}
                  formatter={(v) => [formatFull(v)]} />
                <Bar dataKey="facture" name="Facturé" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="paye" name="Payé" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Main Table */}
          <div className="bg-slate-900 border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/5 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Wallet className="w-5 h-5 text-cyan-400" />
                Suivi détaillé par client — {MONTH_NAMES[month]} {year}
              </h3>
              <span className="text-xs text-slate-500">{data.clients.length} client(s)</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="monthly-payments-table">
                <thead>
                  <tr className="text-[10px] text-slate-500 uppercase border-b border-white/5 bg-slate-950">
                    <th className="text-left px-3 py-3 sticky left-0 bg-slate-950 z-10">#</th>
                    <th className="text-left px-3 py-3 sticky left-8 bg-slate-950 z-10 min-w-[140px]">Client</th>
                    <th className="text-left px-3 py-3">Commune</th>
                    {displayMonths.map(m => {
                      const p = m.split("-");
                      return (
                        <th key={m} className="text-center px-2 py-3 min-w-[100px]" colSpan={2}>
                          <span className="text-cyan-400 font-bold">{MONTH_NAMES[parseInt(p[1])]?.slice(0,3)}</span>
                          <span className="text-slate-600 ml-1">{p[0].slice(2)}</span>
                        </th>
                      );
                    })}
                    <th className="text-right px-3 py-3">Total Fact.</th>
                    <th className="text-right px-3 py-3">Total Payé</th>
                    <th className="text-right px-3 py-3">Reste</th>
                    <th className="text-center px-3 py-3">Taux</th>
                    <th className="text-center px-3 py-3">Détails</th>
                  </tr>
                  <tr className="text-[9px] text-slate-600 border-b border-white/5 bg-slate-950">
                    <th colSpan={3}></th>
                    {displayMonths.map(m => (
                      <React.Fragment key={`sub-${m}`}>
                        <th className="text-center px-1 py-1">Fact.</th>
                        <th className="text-center px-1 py-1">Payé</th>
                      </React.Fragment>
                    ))}
                    <th colSpan={5}></th>
                  </tr>
                </thead>
                <tbody>
                  {data.clients.map((c, i) => (
                    <React.Fragment key={c.pharmacy_id}>
                      <tr className={`border-b border-white/5 hover:bg-white/5 transition-colors ${expandedClient === c.pharmacy_id ? "bg-cyan-500/5" : ""}`}>
                        <td className="px-3 py-3 sticky left-0 bg-inherit z-10">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            i === 0 ? "bg-amber-500 text-slate-900" : i === 1 ? "bg-slate-400 text-slate-900" : i === 2 ? "bg-amber-700 text-white" : "bg-slate-800 text-slate-400"
                          }`}>{i + 1}</span>
                        </td>
                        <td className="px-3 py-3 sticky left-8 bg-inherit z-10">
                          <p className="text-white font-medium text-xs">{c.name}</p>
                          <p className="text-[10px] text-slate-600">{c.total_invoices} factures</p>
                        </td>
                        <td className="px-3 py-3 text-slate-400 text-xs">{c.commune || "-"}</td>
                        {displayMonths.map(m => {
                          const md = c.monthly[m] || {};
                          return (
                            <React.Fragment key={`${c.pharmacy_id}-${m}`}>
                              <td className="text-center px-1 py-3 text-xs text-blue-400 font-medium">
                                {md.invoiced ? formatK(md.invoiced) : <span className="text-slate-700">-</span>}
                              </td>
                              <td className="text-center px-1 py-3 text-xs text-emerald-400 font-medium">
                                {md.paid ? formatK(md.paid) : <span className="text-slate-700">-</span>}
                              </td>
                            </React.Fragment>
                          );
                        })}
                        <td className="px-3 py-3 text-right text-white font-medium text-xs">{formatK(c.total_invoiced)}</td>
                        <td className="px-3 py-3 text-right text-emerald-400 font-medium text-xs">{formatK(c.total_paid)}</td>
                        <td className="px-3 py-3 text-right text-amber-400 font-medium text-xs">{formatK(c.total_remaining)}</td>
                        <td className="px-3 py-3 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <div className="w-12 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${c.recovery_rate >= 80 ? "bg-emerald-500" : c.recovery_rate >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                                style={{ width: `${Math.min(c.recovery_rate, 100)}%` }} />
                            </div>
                            <span className={`text-xs font-bold ${c.recovery_rate >= 80 ? "text-emerald-400" : c.recovery_rate >= 50 ? "text-amber-400" : "text-red-400"}`}>
                              {c.recovery_rate}%
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <button onClick={() => onToggleClient(expandedClient === c.pharmacy_id ? null : c.pharmacy_id)}
                            className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-cyan-400 transition-colors" data-testid={`mp-expand-${i}`}>
                            {expandedClient === c.pharmacy_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>
                      {expandedClient === c.pharmacy_id && (
                        <tr>
                          <td colSpan={100} className="bg-slate-950 px-6 py-4 border-b border-cyan-500/20">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <p className="text-xs text-slate-500 uppercase font-semibold">Informations client</p>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div><span className="text-slate-500">Email:</span> <span className="text-white">{c.email || "-"}</span></div>
                                  <div><span className="text-slate-500">Tél:</span> <span className="text-white">{c.phone || "-"}</span></div>
                                  <div><span className="text-slate-500">Total facturé:</span> <span className="text-blue-400 font-bold">{formatFull(c.total_invoiced)}</span></div>
                                  <div><span className="text-slate-500">Total payé:</span> <span className="text-emerald-400 font-bold">{formatFull(c.total_paid)}</span></div>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <p className="text-xs text-slate-500 uppercase font-semibold">Derniers paiements</p>
                                {(c.recent_payments || []).length === 0 ? (
                                  <p className="text-xs text-slate-600">Aucun paiement enregistré</p>
                                ) : (
                                  <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {c.recent_payments.slice(0, 5).map((p, pi) => (
                                      <div key={pi} className="flex items-center justify-between bg-slate-900 rounded-lg px-3 py-1.5 text-xs">
                                        <div>
                                          <span className="text-white font-medium">{p.method === "especes" ? "Espèces" : p.method || "Paiement"}</span>
                                          <span className="text-slate-600 ml-2">{p.invoice}</span>
                                        </div>
                                        <div className="text-right">
                                          <span className="text-emerald-400 font-bold">+{formatFull(p.amount)}</span>
                                          <p className="text-[9px] text-slate-600">{new Date(p.date).toLocaleDateString("fr-FR")}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-cyan-950/40 border-t-2 border-cyan-500/30 font-bold">
                    <td colSpan={3} className="px-3 py-3 text-cyan-400 text-xs uppercase sticky left-0 bg-cyan-950/40 z-10">Totaux</td>
                    {displayMonths.map(m => {
                      const gt = data.grand_totals[m] || {};
                      return (
                        <React.Fragment key={`total-${m}`}>
                          <td className="text-center px-1 py-3 text-xs text-blue-300 font-bold">{gt.invoiced ? formatK(gt.invoiced) : "-"}</td>
                          <td className="text-center px-1 py-3 text-xs text-emerald-300 font-bold">{gt.paid ? formatK(gt.paid) : "-"}</td>
                        </React.Fragment>
                      );
                    })}
                    <td className="px-3 py-3 text-right text-white text-xs">{formatK(data.summary.total_invoiced)}</td>
                    <td className="px-3 py-3 text-right text-emerald-300 text-xs">{formatK(data.summary.total_paid)}</td>
                    <td className="px-3 py-3 text-right text-amber-300 text-xs">{formatK(data.summary.total_remaining)}</td>
                    <td className="px-3 py-3 text-center text-cyan-300 text-xs">{data.summary.avg_recovery_rate}%</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {data.clients.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Wallet className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aucun client avec des paiements</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
